import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import { resolveMessageTimestamp } from '../chat/messageTime.ts'
import { touchContactLastMessage } from './pairingStore.ts'

export interface ChatMessage {
  id: string
  contactId: string
  text: string
  outgoing: boolean
  timestamp: number
  pending?: boolean
  replyToId?: string
  replyPreview?: string
  mediaKind?: string
  mediaMime?: string
  mediaFileName?: string
  /** Payload documento compresso con GZIP prima della cifratura E2E. */
  mediaGzip?: boolean
  /** Dimensione originale del file prima della compressione. */
  mediaOrigSize?: number
  /** Feedback UI durante compressione/cifratura documenti in uscita. */
  processingStatus?: string
  /** Messaggio in chat a tempo (non E2E). */
  isTemp?: boolean
  /** Durata view-TTL in ms (messaggi effimeri). */
  viewTtlMs?: number
  /** Durata countdown in secondi. */
  durationSeconds?: number
  /** Timestamp ms avvio countdown UI (0 / assente = idle). */
  countdownStartedAt?: number
  /** Mittente: peer ha letto e distrutto (MSG_READ_ACK). */
  readAndDestroyed?: boolean
  /** Durata vocale in ms (UI), indipendente dal file. */
  audioDurationMs?: number
}

interface ChatDb extends DBSchema {
  messages: {
    key: string
    value: ChatMessage
    indexes: { 'by-contact': string }
  }
}

let dbPromise: Promise<IDBPDatabase<ChatDb>> | null = null

function getDb(): Promise<IDBPDatabase<ChatDb>> {
  if (!dbPromise) {
    dbPromise = openDB<ChatDb>('zion-chats', 1, {
      upgrade(db) {
        const store = db.createObjectStore('messages', { keyPath: 'id' })
        store.createIndex('by-contact', 'contactId')
      },
    })
  }
  return dbPromise
}

export async function loadMessages(contactId: string): Promise<ChatMessage[]> {
  const db = await getDb()
  const rows = await db.getAllFromIndex('messages', 'by-contact', contactId)
  // Ripara timestamp sballati (id epoch vs timestamp corrotto) — una volta per sessione chat
  for (const m of rows) {
    const fixed = resolveMessageTimestamp({
      envelopeTs: m.timestamp,
      messageId: m.id,
      fallback: m.timestamp,
    })
    const idNum = Number.parseInt(m.id, 10)
    const preferId =
      Number.isFinite(idNum) &&
      Math.abs(m.timestamp - idNum) > 5 * 60_000 &&
      resolveMessageTimestamp({ messageId: m.id }) === idNum
    const nextTs = preferId ? idNum : fixed
    if (nextTs !== m.timestamp) {
      m.timestamp = nextTs
      await db.put('messages', m)
    }
  }
  return rows.sort((a, b) => {
    const ta = a.timestamp - b.timestamp
    if (ta !== 0) return ta
    const na = Number.parseInt(a.id, 10)
    const nb = Number.parseInt(b.id, 10)
    if (!Number.isNaN(na) && !Number.isNaN(nb) && na !== nb) return na - nb
    return a.id.localeCompare(b.id)
  })
}

export async function getMessageById(id: string): Promise<ChatMessage | undefined> {
  const db = await getDb()
  return db.get('messages', id)
}

export async function messageExists(id: string): Promise<boolean> {
  const db = await getDb()
  return (await db.getKey('messages', id)) != null
}

/** Anteprima riga Home (testo / media) — senza ciphertext grezzo. */
export function previewLineFromMessage(msg: {
  text?: string
  mediaKind?: string
  mediaFileName?: string
}): string {
  if (msg.mediaKind) {
    if (msg.mediaKind === 'audio' || msg.mediaKind === 'voice') return 'Messaggio vocale'
    if (msg.mediaKind === 'image') return 'Foto'
    if (msg.mediaKind === 'video') return 'Video'
    return msg.mediaFileName?.trim() || 'Allegato'
  }
  let text = String(msg.text ?? '')
    .replace(/\s+/g, ' ')
    .trim()
  if (!text) return ''
  if (text.length > 120 && /^[A-Za-z0-9+/=_-]{40,}$/.test(text.replace(/\s/g, ''))) {
    return 'Messaggio'
  }
  if (text.length > 72) text = `${text.slice(0, 72)}…`
  return text
}

export async function appendMessage(msg: ChatMessage): Promise<void> {
  const db = await getDb()
  await db.put('messages', msg)
  // Aggiorna lastMessage + orario sul contatto → Home riordina subito
  void touchContactLastMessage(msg.contactId, msg.timestamp, previewLineFromMessage(msg))
  notifyMessagesChanged(msg.contactId)
}

type MessageListener = (contactId: string) => void
const messageListeners = new Set<MessageListener>()

export function onMessagesChanged(fn: MessageListener): () => void {
  messageListeners.add(fn)
  return () => messageListeners.delete(fn)
}

function notifyMessagesChanged(contactId: string): void {
  for (const fn of messageListeners) fn(contactId)
}

export async function updateMessageText(id: string, text: string, pending = false): Promise<void> {
  const db = await getDb()
  const existing = await db.get('messages', id)
  if (!existing) return
  await db.put('messages', { ...existing, text, pending })
  notifyMessagesChanged(existing.contactId)
}

export async function patchMessage(id: string, patch: Partial<ChatMessage>): Promise<void> {
  const db = await getDb()
  const existing = await db.get('messages', id)
  if (!existing) return
  await db.put('messages', { ...existing, ...patch })
  notifyMessagesChanged(existing.contactId)
}

export async function deleteMessage(id: string): Promise<void> {
  const db = await getDb()
  const existing = await db.get('messages', id)
  if (!existing) return
  await db.delete('messages', id)
  notifyMessagesChanged(existing.contactId)
}

export async function deleteMessagesForContact(contactId: string): Promise<void> {
  const db = await getDb()
  const rows = await db.getAllFromIndex('messages', 'by-contact', contactId)
  const tx = db.transaction('messages', 'readwrite')
  for (const row of rows) {
    await tx.store.delete(row.id)
  }
  await tx.done
  notifyMessagesChanged(contactId)
}

export async function listConversationPeers(): Promise<string[]> {
  const db = await getDb()
  const all = await db.getAll('messages')
  return [...new Set(all.map((m) => m.contactId))]
}

export async function getAllMessages(): Promise<ChatMessage[]> {
  const db = await getDb()
  return db.getAll('messages')
}

export async function replaceAllMessages(messages: ChatMessage[]): Promise<void> {
  const db = await getDb()
  await db.clear('messages')
  for (const msg of messages) {
    await db.put('messages', msg)
  }
  notifyMessagesChanged('*')
}

export async function clearAllMessages(): Promise<void> {
  const db = await getDb()
  await db.clear('messages')
}
